import { LitElement, html, css } from 'lit'
import {
    ScheduleEntry,
    SimplifiedPortCall,
} from '../../../src/models/types/voyage-schedules-model.types'
import { customElement, property } from 'lit/decorators'
import { deepCopy } from '../../../src/utils/helpers'
import { VoyageSchedulesModel } from '../../../src/models/voyage-schedules-model'
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons'
import { VesselPlanningModel } from '../../../src/models/vessel-planning-model'
import {
    _formatDate,
    generateMessage,
    getTimeThresholds,
    styles,
} from '../utils/vessel-departure-utils'

@customElement('vessel-predeparture-popup')
export class VesselPredeparturePopup extends LitElement {
    @property({ attribute: false }) _schedule: ScheduleEntry[] = []
    private _refreshInterval: number = 0
    @property({ type: Number }) _timeLeftBeforeEtd: number = 0
    @property({ attribute: false }) currentDateInArrivalTerminal: string = ''
    @property({ type: Boolean, reflect: false }) close: boolean = false
    private _portOmitsMap: Map<string, SimplifiedPortCall> = new Map()

    static get styles() {
        return [styles]
    }

    constructor() {
        super()
        this._refreshSchedule = this._refreshSchedule.bind(this)
    }
    connectedCallback() {
        super.connectedCallback()

        VesselPlanningModel!.addEventListener(
            'plan-changed',
            this._refreshSchedule,
        )
        VoyageSchedulesModel!.addEventListener(
            'schedule-updated',
            this._refreshSchedule,
        )

        this._refreshSchedule()
    }

    disconnectedCallback() {
        super.disconnectedCallback()

        VesselPlanningModel!.removeEventListener(
            'plan-changed',
            this._refreshSchedule,
        )
        VoyageSchedulesModel!.removeEventListener(
            'schedule-updated',
            this._refreshSchedule,
        )

        if (this._refreshInterval) {
            clearInterval(this._refreshInterval)
            this._refreshInterval = 0
        }
    }
    private async _refreshSchedule() {
        const schedule = deepCopy(
            (await VoyageSchedulesModel!.getScheduleFor(
                (<any>window)['appenv'].vesselIMONumber,
            )) || [],
        )
        this._portOmitsMap.clear()
        const cutoffDate = Date.now() + 12 * 7 * 24 * 60 * 60 * 1000
        const getArrivalTimestamp = (entry: ScheduleEntry) => {
            const arrivalAt = entry.arrivalPortCall?.GSIS?.arrivalAt || entry.arrivalAt
            const timestamp = new Date(arrivalAt).getTime()
            return Number.isFinite(timestamp) ? timestamp : 0
        }
        const filteredSchedule = schedule.filter(
            (entry) => getArrivalTimestamp(entry) <= cutoffDate,
        )
        for (let i = 0; i < filteredSchedule.length; i++) {
            const entry = filteredSchedule[i]
            if (entry.omitted) {
                if (i > 0) {
                    this._portOmitsMap.set(
                        filteredSchedule[i - 1].id,
                        entry.arrivalPortCall!.current,
                    )
                }
                filteredSchedule.splice(i, 1)
                i--
            }
        }
        this._schedule = filteredSchedule
        this.renderData()
    }

    private async getPlansStatus(itemId: string): Promise<boolean> {
        try {
            const plans = await VesselPlanningModel!.getPlans(itemId)
            return plans && plans.length > 0 && plans[0].status !== 'approved'
        } catch (err) {
            console.error(err)
            return false
        }
    }


    private renderData() {
        const timeThresholds = getTimeThresholds()
        if (this._schedule.length > 0 && this.close === false) {
            this._schedule.map(async (item) => {
                const etdTime = new Date(item.departureAt).getTime();
                const currentTime = new Date().getTime()
                if (
                    etdTime <= currentTime + timeThresholds.oneHour &&
                    etdTime > currentTime
                ) {
                    if (await this.getPlansStatus(item.id)) {
                        this._timeLeftBeforeEtd = 1
                    }
                } else if (
                    etdTime <= currentTime + timeThresholds.twelveHours &&
                    etdTime > currentTime
                ) {
                    if (await this.getPlansStatus(item.id)) {
                        this._timeLeftBeforeEtd = 12
                    }
                } else if (
                    etdTime <= currentTime + timeThresholds.twentyFourHours &&
                    etdTime > currentTime
                ) {
                    if (await this.getPlansStatus(item.id)) {
                        this._timeLeftBeforeEtd = 24
                    }
                } else if (
                    etdTime <= currentTime + timeThresholds.thirtySixHours &&
                    etdTime > currentTime
                ) {
                    if (await this.getPlansStatus(item.id)) {
                        this._timeLeftBeforeEtd = 36
                    }
                } else if (
                    etdTime <= currentTime + timeThresholds.fortyEightHours &&
                    etdTime > currentTime
                ) {
                    if (await this.getPlansStatus(item.id)) {
                        this._timeLeftBeforeEtd = 48
                    }
                } else {
                    this._timeLeftBeforeEtd = 0
                }
            })
        }
    }

    render() {
        let message;
        message = generateMessage(this._timeLeftBeforeEtd)
        const formattedDate = _formatDate(new Date())
        return html`${this._timeLeftBeforeEtd > 0
            ? html`
                  <div class="grid-container para-style">
                      <div class="icon-container">
                          <div class="icon-styles">${SvgIcons.infoIcon}</div>
                      </div>
                      <div>
                          <div>
                              <span class="title-style"
                                  >You have not submitted a voyage plan via
                                  StarConnect </span
                              >| ${formattedDate}
                          </div>
                          <div class="message-styles">${message}</div>
                      </div>
                      <div class="icon-container">
                          <button
                              class="closeButton"
                              @click=${() => {
                                  this.close = true
                                  this._timeLeftBeforeEtd = 0
                              }}
                          >
                              ${SvgIcons.lightTimes}
                          </button>
                      </div>
                  </div>
              `
            : html``}`
    }
}
